<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-static-inline-styles, vue/custom-event-name-casing, vue/no-root-v-if, vue/html-closing-bracket-newline -->
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-0">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="$emit('close')" />
    
    <!-- Modal -->
    <div class="relative bg-n-surface-1 rounded-2xl shadow-2xl w-[calc(100vw-1.5rem)] sm:w-[720px] max-h-[92vh] sm:max-h-[85vh] flex flex-col sm:flex-row overflow-hidden border border-n-weak">
      <!-- Disconnect loading overlay (inside modal, above all content) -->
      <div
        v-if="isDisconnecting"
        class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-n-surface-1/80 backdrop-blur-sm rounded-2xl"
      >
        <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
        <span class="text-sm text-n-slate-11">Removendo conta...</span>
      </div>
      <!-- Left Nav -->
      <nav class="w-full sm:w-56 bg-n-surface-2 sm:border-r border-b sm:border-b-0 border-n-weak p-3 sm:p-4 flex sm:flex-col gap-2 sm:gap-1 overflow-x-auto">
        <div class="hidden sm:flex items-center gap-2 mb-4">
          <span class="i-lucide-settings size-5 text-n-slate-11" />
          <h2 class="text-sm font-bold text-n-slate-12">Configurações do calendário</h2>
        </div>
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          class="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left whitespace-nowrap"
          :class="activeTab === tab.id ? 'bg-n-brand/10 text-n-brand' : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'"
          @click="activeTab = tab.id"
        >
          <span :class="tab.icon" class="size-4" />
          {{ tab.label }}
        </button>
      </nav>

      <!-- Content -->
      <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 sm:p-5 border-b border-n-weak">
          <div class="flex items-center gap-2">
            <span :class="currentTab.icon" class="size-5 text-n-slate-11" />
            <h3 class="text-base sm:text-lg font-bold text-n-slate-12">{{ currentTab.label }}</h3>
          </div>
          <button 
            class="size-8 flex items-center justify-center rounded-lg hover:bg-n-alpha-1 text-n-slate-11 transition-colors"
            @click="$emit('close')"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <!-- Tab Content -->
        <div class="flex-1 overflow-y-auto p-4 sm:p-5">
          <!-- Aparência -->
          <div v-if="activeTab === 'appearance'" class="space-y-6">
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12 mb-1">Estilo dos eventos</h4>
              <p class="text-xs text-n-slate-9 mb-4">Escolha como os eventos serão exibidos no calendário</p>
              <div class="flex flex-col sm:flex-row gap-3">
                <button 
                  class="flex-1 p-4 rounded-xl border-2 transition-all text-left"
                  :class="localColorMode === 'subtle' ? 'border-n-brand bg-n-brand/5' : 'border-n-weak hover:border-n-slate-7'"
                  @click="localColorMode = 'subtle'"
                >
                  <div class="flex items-center gap-2 mb-2">
                    <div class="w-full h-6 rounded" style="background-color: rgba(66, 133, 244, 0.15); border-left: 3px solid #4285f4;">
                      <span class="text-[10px] font-medium px-2 leading-6" style="color: #4285f4;">Evento exemplo</span>
                    </div>
                  </div>
                  <span class="text-sm font-semibold text-n-slate-12">Sutil</span>
                  <p class="text-[11px] text-n-slate-9 mt-0.5">Cores suaves e discretas</p>
                </button>
                <button 
                  class="flex-1 p-4 rounded-xl border-2 transition-all text-left"
                  :class="localColorMode === 'solid' ? 'border-n-brand bg-n-brand/5' : 'border-n-weak hover:border-n-slate-7'"
                  @click="localColorMode = 'solid'"
                >
                  <div class="flex items-center gap-2 mb-2">
                    <div class="w-full h-6 rounded" style="background-color: #4285f4; border-left: 3px solid #3367d6;">
                      <span class="text-[10px] font-medium px-2 leading-6 text-white">Evento exemplo</span>
                    </div>
                  </div>
                  <span class="text-sm font-semibold text-n-slate-12">Sólida</span>
                  <p class="text-[11px] text-n-slate-9 mt-0.5">Cores vibrantes e destacadas</p>
                </button>
              </div>
            </div>

            <div>
              <h4 class="text-sm font-semibold text-n-slate-12 mb-1">Cor dos eventos</h4>
              <p class="text-xs text-n-slate-9 mb-3">Os eventos utilizarão a cor definida no Google Calendar para cada calendário.</p>
              <div class="flex items-center gap-2 px-3 py-2 bg-n-alpha-1 rounded-lg">
                <span class="i-lucide-palette size-4 text-n-slate-11" />
                <span class="text-sm text-n-slate-11">Usar cor do calendário</span>
                <span class="i-lucide-check size-4 text-n-brand ml-auto" />
              </div>
            </div>
          </div>

          <!-- Contas -->
          <div v-if="activeTab === 'accounts'" class="space-y-6">
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12 mb-1">Contas</h4>
              <p class="text-xs text-n-slate-9 mb-4">Gerencie suas contas conectadas</p>
              
              <div class="space-y-3">
                <div 
                  v-for="account in connectedAccounts" 
                  :key="account.id"
                  class="flex items-center gap-3 p-3 border border-n-weak rounded-xl bg-n-surface-2/50"
                >
                  <svg class="size-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#4285F4" d="M21.5 2h-4V0h-2v2h-7V0H6.5v2h-4A2.5 2.5 0 0 0 0 4.5v17A2.5 2.5 0 0 0 2.5 24h19a2.5 2.5 0 0 0 2.5-2.5v-17A2.5 2.5 0 0 0 21.5 2Zm.5 19.5a.5.5 0 0 1-.5.5h-19a.5.5 0 0 1-.5-.5V9h20v12.5ZM2 7V4.5a.5.5 0 0 1 .5-.5h4V6h2V4h7v2h2V4h4a.5.5 0 0 1 .5.5V7H2Z"/><path fill="#34A853" d="M7 12H5v2h2v-2Z"/><path fill="#FBBC04" d="M13 12h-2v2h2v-2Z"/><path fill="#EA4335" d="M19 12h-2v2h2v-2Z"/><path fill="#4285F4" d="M7 16H5v2h2v-2Z"/><path fill="#34A853" d="M13 16h-2v2h2v-2Z"/><path fill="#EA4335" d="M19 16h-2v2h2v-2Z"/></svg>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-n-slate-12 truncate">{{ account.email }}</p>
                    <p class="text-xs text-n-slate-9 flex items-center gap-1">
                      Google Agenda
                      <span v-if="account.status === 'enabled'" class="inline-flex items-center gap-1 text-green-600">
                        · <span class="size-1.5 rounded-full bg-green-500 inline-block" /> Conectado
                      </span>
                    </p>
                  </div>
                  <div class="relative">
                    <button 
                      class="size-8 flex items-center justify-center rounded-lg hover:bg-n-alpha-1 text-n-slate-9 transition-colors"
                      @click="activeAccountMenu = activeAccountMenu === account.id ? null : account.id"
                    >
                      <span class="i-lucide-more-horizontal size-4" />
                    </button>
                    <!-- Popover Menu -->
                    <div 
                      v-if="activeAccountMenu === account.id"
                      class="absolute right-0 top-10 w-48 bg-n-surface-1 border border-n-weak rounded-xl shadow-lg z-10 py-1 overflow-hidden"
                    >
                      <button 
                        class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 text-left transition-colors"
                        @click="$emit('sync-account', account.id); activeAccountMenu = null"
                      >
                        <span class="i-lucide-refresh-cw size-4" />
                        Sincronização forçada
                      </button>
                      <button
                        class="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="isDisconnecting"
                        @click="onDisconnectClick(account.id)"
                      >
                        <span class="i-lucide-trash-2 size-4" />
                        Remover conta
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <button 
                class="w-full flex items-center justify-center gap-2 mt-3 py-2.5 border border-dashed border-n-weak rounded-xl text-sm text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 transition-colors"
                @click="$emit('connect')"
              >
                <span class="i-lucide-plus size-4" />
                Adicionar conta
              </button>
            </div>

            <div class="border-t border-n-weak pt-4">
              <h4 class="text-sm font-semibold text-n-slate-12 mb-1">Calendário padrão</h4>
              <p class="text-xs text-n-slate-9 mb-3">Selecione o calendário que será usado como calendário padrão</p>
              <ComboBox
                :model-value="defaultCalendarId"
                :options="defaultCalendarOptions"
                placeholder="Selecione um calendário"
                search-placeholder="Buscar calendário..."
                empty-state="Nenhum calendário encontrado"
                use-fixed-dropdown
                class="[&>div>button]:h-10 [&>div>button]:!bg-n-surface-2"
                @update:model-value="val => $emit('set-default', val)"
              />
            </div>
          </div>

          <!-- Fuso horário -->
          <div v-if="activeTab === 'timezone'" class="space-y-6">
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12 mb-1">Fuso horário do calendário</h4>
              <p class="text-xs text-n-slate-9 mb-4">
                Define o fuso horário usado para exibir e interpretar os horários dos eventos e lembretes deste calendário.
              </p>

              <div class="space-y-4">
                <div>
                  <label class="text-xs font-semibold text-n-slate-11 block mb-2 uppercase">Calendário</label>
                  <ComboBox
                    v-model="selectedTimezoneCalendarId"
                    :options="manageableCalendarOptions"
                    placeholder="Selecione um calendário"
                    search-placeholder="Buscar calendário..."
                    empty-state="Nenhum calendário disponível"
                    use-fixed-dropdown
                    class="[&>div>button]:h-10 [&>div>button]:!bg-n-surface-2"
                  />
                </div>

                <div v-if="selectedTimezoneCalendarId">
                  <label class="text-xs font-semibold text-n-slate-11 block mb-2 uppercase">Fuso horário</label>
                  <multiselect
                    v-model="selectedTimezone"
                    :options="timezoneOptions"
                    track-by="value"
                    label="label"
                    placeholder="Selecione o fuso horário"
                    :allow-empty="false"
                    selected-label=""
                    select-label=""
                    deselect-label=""
                    class="multiselect--timezone"
                  />
                </div>

                <div v-if="selectedTimezoneCalendarId" class="flex justify-end">
                  <span
                    class="text-xs font-medium"
                    :class="timezoneSaving ? 'text-n-blue-11' : 'text-n-slate-10'"
                  >
                    {{ timezoneSaving ? 'Salvando fuso horário...' : 'Salvamento automático' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Permissões -->
          <div v-if="activeTab === 'permissions'" class="space-y-6">
            <div>
              <h4 class="text-sm font-semibold text-n-slate-12 mb-1">Permissões por calendário</h4>
              <p class="text-xs text-n-slate-9 mb-4">
                Vincule calendários às caixas de entrada permitidas.
              </p>

              <div class="space-y-4">
                <div>
                  <label class="text-xs font-semibold text-n-slate-11 block mb-2 uppercase">Calendário</label>
                  <ComboBox
                    v-model="selectedPermissionCalendarId"
                    :options="manageableCalendarOptions"
                    placeholder="Selecione um calendário"
                    search-placeholder="Buscar calendário..."
                    empty-state="Nenhum calendário disponível"
                    use-fixed-dropdown
                    class="[&>div>button]:h-10 [&>div>button]:!bg-n-surface-2"
                  />
                </div>

                <div>
                  <label class="text-xs font-semibold text-n-slate-11 block mb-2 uppercase">Caixas de entrada</label>
                  <TagMultiSelectComboBox
                    v-model="selectedPermissionInboxIds"
                    :options="inboxOptions"
                    placeholder="Selecione as inboxes"
                    search-placeholder="Buscar inbox"
                    empty-state="Nenhuma inbox encontrada"
                    use-fixed-dropdown
                  />
                </div>

                <div class="flex justify-end">
                  <span
                    class="text-xs font-medium"
                    :class="permissionsSaving ? 'text-n-blue-11' : 'text-n-slate-10'"
                  >
                    {{ permissionsSaving ? 'Salvando permissões...' : 'Salvamento automático' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { useAlert } from 'dashboard/composables';
import { timeZoneOptions } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';

export default {
  name: 'CalendarSettingsModal',
  components: {
    ComboBox,
    TagMultiSelectComboBox,
  },
  props: {
    show: { type: Boolean, default: false },
    accountId: { type: [Number, String], required: true },
    connectedAccounts: { type: Array, default: () => [] },
    allCalendars: { type: Array, default: () => [] },
    allInboxes: { type: Array, default: () => [] },
    colorMode: { type: String, default: 'subtle' },
    defaultCalendarId: { type: [Number, String], default: null },
    initialTab: { type: String, default: 'appearance' },
  },
  emits: ['close', 'connect', 'disconnect', 'set-default', 'update:colorMode', 'sync-account', 'calendarUpdated'],
  data() {
    return {
      activeTab: this.initialTab,
      localColorMode: this.colorMode,
      activeAccountMenu: null,
      isDisconnecting: false,
      selectedPermissionCalendarId: null,
      selectedPermissionInboxIds: [],
      permissionsLoading: false,
      permissionsSaving: false,
      isHydratingPermissionInboxes: false,
      lastSavedPermissionInboxIds: [],
      savePermissionsTimer: null,
      selectedTimezoneCalendarId: null,
      selectedTimezone: null,
      timezoneSaving: false,
      timezoneTimer: null,
      lastSavedTimezone: null,
      timezoneOptions: timeZoneOptions(),
      tabs: [
        { id: 'appearance', label: 'Aparência', icon: 'i-lucide-paintbrush' },
        { id: 'accounts', label: 'Contas', icon: 'i-lucide-users' },
        { id: 'permissions', label: 'Permissões', icon: 'i-lucide-shield' },
        { id: 'timezone', label: 'Fuso horário', icon: 'i-lucide-clock' },
      ],
    };
  },
  mounted() {
    document.addEventListener('click', this.closeAccountMenu);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeAccountMenu);
    if (this.savePermissionsTimer) {
      clearTimeout(this.savePermissionsTimer);
      this.savePermissionsTimer = null;
    }
    if (this.timezoneTimer) {
      clearTimeout(this.timezoneTimer);
      this.timezoneTimer = null;
    }
  },
  computed: {
    currentTab() {
      return this.tabs.find(t => t.id === this.activeTab) || this.tabs[0];
    },
    defaultCalendarOptions() {
      return (this.allCalendars || []).map(calendar => ({
        value: calendar.id,
        label: calendar.name,
      }));
    },
    manageableCalendarOptions() {
      return (this.allCalendars || [])
        .filter(calendar => calendar.can_manage_settings)
        .map(calendar => ({
          value: calendar.id,
          label: calendar.name,
        }));
    },
    inboxOptions() {
      const dedupe = new Map();
      (this.allInboxes || []).forEach(inbox => {
        if (!inbox || !inbox.id || !inbox.name) return;
        if (inbox.deleted_at) return;
        if (!dedupe.has(inbox.id)) {
          dedupe.set(inbox.id, { value: inbox.id, label: inbox.name });
        }
      });
      return Array.from(dedupe.values()).sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  methods: {
    onDisconnectClick(accountId) {
      this.activeAccountMenu = null;
      this.isDisconnecting = true;
      this.$emit('disconnect', accountId, () => {
        this.isDisconnecting = false;
      });
    },
    closeAccountMenu(e) {
      if (this.activeAccountMenu && !e.target.closest('.relative')) {
        this.activeAccountMenu = null;
      }
    },
    ensureSelectedPermissionCalendar() {
      if (this.selectedPermissionCalendarId && this.manageableCalendarOptions.some(c => Number(c.value) === Number(this.selectedPermissionCalendarId))) {
        return;
      }
      this.selectedPermissionCalendarId = this.manageableCalendarOptions[0]?.value || null;
    },
    async fetchCalendarInboxes() {
      if (!this.selectedPermissionCalendarId) {
        this.selectedPermissionInboxIds = [];
        this.lastSavedPermissionInboxIds = [];
        return;
      }
      this.permissionsLoading = true;
      this.isHydratingPermissionInboxes = true;
      try {
        const response = await window.axios.get(
          `/api/v1/accounts/${this.accountId}/calendars/${this.selectedPermissionCalendarId}/inboxes`
        );
        const ids = (response.data || []).map(inbox => Number(inbox.id)).filter(Boolean);
        this.selectedPermissionInboxIds = ids;
        this.lastSavedPermissionInboxIds = [...ids].sort((a, b) => a - b);
      } catch (error) {
        useAlert('Erro ao carregar inboxes vinculadas ao calendário.');
      } finally {
        this.isHydratingPermissionInboxes = false;
        this.permissionsLoading = false;
      }
    },
    sortedUniqueIds(ids) {
      return Array.from(new Set((ids || []).map(id => Number(id)).filter(Boolean))).sort((a, b) => a - b);
    },
    hasPermissionSelectionChanged() {
      const current = this.sortedUniqueIds(this.selectedPermissionInboxIds);
      const lastSaved = this.sortedUniqueIds(this.lastSavedPermissionInboxIds);
      if (current.length !== lastSaved.length) return true;
      return current.some((id, idx) => id !== lastSaved[idx]);
    },
    scheduleAutoSavePermissions() {
      if (this.savePermissionsTimer) {
        clearTimeout(this.savePermissionsTimer);
      }
      this.savePermissionsTimer = setTimeout(() => {
        this.savePermissionsTimer = null;
        this.saveCalendarInboxes();
      }, 500);
    },
    loadTimezoneForCalendar() {
      if (!this.selectedTimezoneCalendarId) return;
      const calendar = (this.allCalendars || []).find(c => Number(c.id) === Number(this.selectedTimezoneCalendarId));
      const tz = calendar?.timezone || 'UTC';
      const option = this.timezoneOptions.find(o => o.value === tz) || { label: tz, value: tz };
      this.selectedTimezone = option;
      this.lastSavedTimezone = option.value;
    },
    scheduleAutoSaveTimezone() {
      if (this.timezoneTimer) clearTimeout(this.timezoneTimer);
      this.timezoneTimer = setTimeout(() => {
        this.timezoneTimer = null;
        this.saveTimezone();
      }, 500);
    },
    async saveTimezone() {
      if (!this.selectedTimezoneCalendarId || !this.selectedTimezone) return;
      if (this.selectedTimezone.value === this.lastSavedTimezone) return;
      this.timezoneSaving = true;
      try {
        const response = await window.axios.patch(
          `/api/v1/accounts/${this.accountId}/calendars/${this.selectedTimezoneCalendarId}/update_timezone`,
          { timezone: this.selectedTimezone.value }
        );
        this.lastSavedTimezone = this.selectedTimezone.value;
        this.$emit('calendarUpdated', response.data);
      } catch (error) {
        useAlert('Erro ao salvar o fuso horário.');
      } finally {
        this.timezoneSaving = false;
      }
    },
    async saveCalendarInboxes() {
      if (!this.selectedPermissionCalendarId) return;
      if (this.permissionsLoading || this.isHydratingPermissionInboxes) return;
      if (!this.hasPermissionSelectionChanged()) return;
      this.permissionsSaving = true;
      try {
        const payloadIds = this.sortedUniqueIds(this.selectedPermissionInboxIds);
        await window.axios.patch(
          `/api/v1/accounts/${this.accountId}/calendars/${this.selectedPermissionCalendarId}/inboxes`,
          { inbox_ids: payloadIds }
        );
        this.lastSavedPermissionInboxIds = payloadIds;
      } catch (error) {
        useAlert('Erro ao atualizar permissões de inbox.');
      } finally {
        this.permissionsSaving = false;
      }
    },
  },
  watch: {
    initialTab(val) {
      this.activeTab = val;
    },
    colorMode(val) {
      this.localColorMode = val;
    },
    localColorMode(val) {
      this.$emit('update:colorMode', val);
    },
    show(val) {
      if (!val) return;
      this.ensureSelectedPermissionCalendar();
      if (this.activeTab === 'permissions') {
        this.fetchCalendarInboxes();
      }
    },
    activeTab(val) {
      if (val === 'permissions') {
        this.ensureSelectedPermissionCalendar();
        this.fetchCalendarInboxes();
      }
      if (val === 'timezone') {
        if (!this.selectedTimezoneCalendarId) {
          this.selectedTimezoneCalendarId = this.manageableCalendarOptions[0]?.value || null;
        }
        this.loadTimezoneForCalendar();
      }
    },
    selectedPermissionCalendarId() {
      if (this.activeTab === 'permissions') {
        this.fetchCalendarInboxes();
      }
    },
    selectedTimezoneCalendarId() {
      this.loadTimezoneForCalendar();
    },
    selectedTimezone(val) {
      if (!val || val.value === this.lastSavedTimezone) return;
      if (this.activeTab !== 'timezone') return;
      this.scheduleAutoSaveTimezone();
    },
    selectedPermissionInboxIds: {
      handler() {
        if (this.activeTab !== 'permissions') return;
        if (this.isHydratingPermissionInboxes) return;
        this.scheduleAutoSavePermissions();
      },
      deep: true,
    },
    allCalendars: {
      handler() {
        this.ensureSelectedPermissionCalendar();
      },
      deep: true,
    },
  },
};
</script>
